import React, { useEffect, useState, useRef, useMemo, useCallback } from "react";
import { Box } from "@mui/system";
import { useDebriefingStyles } from "../../styles/debriefing.styles";
import {
  Button,
  Checkbox,
  Chip,
  ClickAwayListener,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import OndemandVideoIcon from "@mui/icons-material/OndemandVideo";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import ReactPlayer from "react-player";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import html2canvas from "html2canvas";
import TimerBox from "./TimerBox";
import { useMarkers } from "../../hooks/useMarkers";
import { LoadingButton } from "@mui/lab";
import { IMarker } from "../../../types/Markers";
import { useModal } from "../../../common/hooks/useModal";
import secureLocalStorage from "react-secure-storage";
import { useParams } from "react-router-dom";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import colors from "../../../common/styles/colors";
import palette from "../../../common/styles/palette";
import { useTranslation } from "react-i18next";
import type { AiAnalysis, AiVideoSource } from "../../../services/sheai/SheAiChecklistTypes";
import { transcriptToVtt } from "./transcriptToVtt";
import { cameraLabel, SEEK_VIDEO_EVENT, sourceVideoURL, VideoSeekDetail } from "../../utils/videoSources";

const EMPTY_VIDEOS: any[] = [];

const mediaIdentity = (uri?: string) => {
  const url = sourceVideoURL(uri);
  if (!url) return undefined;
  const parsed = new URL(url);
  parsed.searchParams.delete("generation");
  return parsed.href;
};

export default function DebriefingVideo({
  video,
  videoId,
  arrVideo = EMPTY_VIDEOS,
  setVideo,
  setVideoId,
  cameraMain,
  setCameraMain,
  heightVideo,
  widthVideo,
  notMarker,
  cameraIndex,
  setCameraIndex,
  isExternal, // novo prop opcional
  badge, // nome da sala exibido como pill verde no topo do player
  transcript,
  analysisSources,
  presentation,
  minimized = false,
  onToggleMinimized,
  minimizedContentId,
  onEvidenceSeek,
}: any) {
  const { t } = useTranslation();
  const stylesDebriefing = useDebriefingStyles();
  const {
    setMarkerByDebriefing,
    handleFormatTime,
    storeMarkerData,
    markerData,
    getMarkerByDebriefing,
  } = useMarkers();
  const { handleOpenModal, dataModal } = useModal();

  const { debriefingId } = useParams();

  const profileId = secureLocalStorage.getItem("role") as number | null;

  const [play, setPlay] = useState(false);
  const [progressVideo, setProgressVideo] = useState(0);
  const [markerTime, setMarkerTime] = useState<IMarker[] | boolean>();
  const playerRef = useRef<ReactPlayer | null>(null);
  const playerContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollFrame = useRef<number>();
  useEffect(() => () => { if (scrollFrame.current !== undefined) cancelAnimationFrame(scrollFrame.current); }, []);
  const [captionUrl, setCaptionUrl] = useState<string | null>(null);
  const captionVtt = useMemo(() => transcriptToVtt(transcript as AiAnalysis["transcript"]), [transcript]);
  const sources = useMemo(() => (analysisSources as AiVideoSource[] | undefined || [])
    .map((source) => ({ ...source, url: sourceVideoURL(source.uri, source.generation) }))
    .filter((source) => !!source.url), [analysisSources]);
  const primary = sources.find((source) => source.role === "primary") || sources[0];
  const [selectedSourceId, setSelectedSourceId] = useState<string | undefined>();
  const selectedSource = sources.find((source) => source.source_id === selectedSourceId) || primary;
  const effectiveVideo = selectedSource?.url || video;
  const analysisMode = sources.length > 0;
  const analysisPresentation = presentation === "analysis";
  const positionLabel = `${String(Math.floor(progressVideo / 60)).padStart(2, "0")}:${String(progressVideo % 60).padStart(2, "0")}`;
  const captionsForSource = !analysisMode || selectedSource?.source_id === primary?.source_id;
  const activeCaptionUrl = captionsForSource ? captionUrl : null;
  // ReactPlayer mantém config.file em cache pela URL (memoizeOne por url/key):
  // aguarda o Blob URL quando a transcrição já veio e inclui a legenda ativa na
  // key caso a transcrição chegue depois da mídia (ex.: aba Checklist).
  const waitingForCaptions = captionsForSource && !!captionVtt && !captionUrl;
  const playerKey = `${effectiveVideo || ""}|${activeCaptionUrl || ""}`;
  // O default do <track> pode não reativar a legenda após trocar de câmera.
  const applyCaptionMode = useCallback(() => {
    const media = playerRef.current?.getInternalPlayer() as HTMLVideoElement | undefined;
    if (!media?.textTracks) return;
    Array.from(media.textTracks).forEach((track) => {
      if (track.label === "Português" && track.kind === "captions") {
        track.mode = activeCaptionUrl ? "showing" : "disabled";
      }
    });
  }, [activeCaptionUrl]);
  useEffect(() => { applyCaptionMode(); }, [applyCaptionMode, effectiveVideo]);
  useEffect(() => { if (minimized) setPlay(false); }, [minimized]);
  // Só associa marcações quando a URL coincide com uma gravação já cadastrada.
  // source_id identifica a câmera, não um streaming/attachment persistido.
  const matchedVideo = analysisMode ? arrVideo.find((item) =>
    mediaIdentity(item.recording_url || item.static_url) === mediaIdentity(selectedSource?.url)
  ) : undefined;
  const markerVideoId = analysisMode
    ? matchedVideo?.streaming_id || matchedVideo?.attachment_id ||
      (mediaIdentity(video) === mediaIdentity(selectedSource?.url) ? videoId : undefined)
    : videoId;
  const markerSourceAvailable = !analysisMode || !!markerVideoId;
  const markersEnabled = !notMarker && markerSourceAvailable;
  const activePlayerKey = useRef(playerKey);
  const activeUrl = useRef(effectiveVideo);
  const readyUrl = useRef<string>();
  if (activePlayerKey.current !== playerKey) {
    readyUrl.current = undefined;
    activePlayerKey.current = playerKey;
    activeUrl.current = effectiveVideo;
  }
  const pendingSeek = useRef<{ url: string; seconds: number; play?: boolean }>();
  const sourcesKey = sources.map((source) => `${source.source_id}:${source.url}`).join("|");

  useEffect(() => {
    setSelectedSourceId(primary?.source_id);
    setPlay(false);
    setProgressVideo(0);
    pendingSeek.current = undefined;
  }, [sourcesKey]);

  useEffect(() => {
    const seek = (event: Event) => {
      const request = event as CustomEvent<VideoSeekDetail>;
      if (request.defaultPrevented || !Number.isFinite(request.detail?.seconds) || request.detail.seconds < 0) return;
      const source = request.detail.sourceId
        ? sources.find((item) => item.source_id === request.detail.sourceId)
        : primary;
      if (request.detail.sourceId && !source) return;
      const url = source?.url || (!request.detail.sourceId ? video : undefined);
      if (!url) return;
      request.preventDefault();
      onEvidenceSeek?.();
      if (scrollFrame.current !== undefined) cancelAnimationFrame(scrollFrame.current);
      // Após expandir/trocar a fonte, alinha o player abaixo do cabeçalho fixo.
      scrollFrame.current = requestAnimationFrame(() => {
        playerContainerRef.current?.scrollIntoView({ block: "start", behavior: "smooth" });
        scrollFrame.current = undefined;
      });
      if (source) setSelectedSourceId(source.source_id);
      setPlay(true);
      setProgressVideo(request.detail.seconds);
      if (readyUrl.current === url && activeUrl.current === url) {
        playerRef.current?.seekTo(request.detail.seconds, "seconds");
      } else {
        pendingSeek.current = { url, seconds: request.detail.seconds, play: true };
      }
    };
    window.addEventListener(SEEK_VIDEO_EVENT, seek);
    return () => window.removeEventListener(SEEK_VIDEO_EVENT, seek);
  }, [sources, primary, video, onEvidenceSeek]);

  const handleReady = () => {
    // Um onReady tardio do vídeo anterior não pode consumir o salto da câmera nova.
    if (activeUrl.current !== effectiveVideo) return;
    readyUrl.current = effectiveVideo;
    applyCaptionMode();
    const pending = pendingSeek.current;
    if (pending && pending.url === effectiveVideo) {
      playerRef.current?.seekTo(pending.seconds, "seconds");
      if (pending.play) setPlay(true);
      pendingSeek.current = undefined;
    }
  };

  useEffect(() => {
    if (!captionVtt) {
      setCaptionUrl(null);
      return;
    }

    const url = URL.createObjectURL(new Blob([captionVtt], { type: "text/vtt" }));
    setCaptionUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [captionVtt]);

  const handleProgress = (state: any) => {
    setProgressVideo(parseInt(state.playedSeconds));
  };

  const getTimeVideo = () => {
    let seconds = progressVideo;
    handleCaptureFrame(seconds);
  };

  const handleCaptureFrame = async (seconds: any) => {
    if (markersEnabled && playerRef.current && Array.isArray(markerTime)) {
      const videoElement =
        playerRef.current.getInternalPlayer() as HTMLVideoElement;

      videoElement.currentTime = seconds;

      await new Promise((resolve) => {
        videoElement.onseeked = resolve;
      });

      html2canvas(videoElement).then((canvas) => {
        const frameUrl = canvas.toDataURL();
        let arrTime = markerTime;
        const data = {
          time: seconds,
          base64_data: frameUrl.split("data:image/png;base64,")[1],
        };

        arrTime.push(data);
        setMarkerTime(arrTime);
        setMarkerByDebriefing({
          base64_data: data.base64_data,
          time: handleFormatTime(data.time),
          video_id: markerVideoId,
        });
      });
    }
  };

  const handleModal = (data: IMarker) => {
    handleOpenModal({
      displayName: "editScreenshot",
      data: data,
    });
  };

  const changeCamera = (index: any) => {
    if (analysisMode) {
      const source = sources[index];
      if (!source) return;
      setSelectedSourceId(source.source_id);
      setPlay(false);
      setProgressVideo(0);
      // Sem sincronização conhecida, cada câmera começa no próprio tempo zero.
      if (readyUrl.current === source.url && activeUrl.current === source.url) {
        playerRef.current?.seekTo(0, "seconds");
      } else pendingSeek.current = { url: source.url!, seconds: 0 };
      return;
    }
    const arr = arrVideo;
    setVideo("");
    setPlay(false);
    setVideo(arr[index]?.recording_url);
    setVideoId(arr[index]?.streaming_id);
    setCameraMain(arr[index]?.camera_main);
    setCameraIndex(index + 1);
    getMarkerByDebriefing(debriefingId, arr[index]?.streaming_id);

    if (playerRef.current && !play) pendingSeek.current = {
      url: arr[index]?.recording_url, seconds: progressVideo, play: true,
    };
  };

  useEffect(() => {
    if (!markerSourceAvailable) {
      setMarkerTime([]);
      return;
    }
    setMarkerTime(markerData);
  }, [markerData, markerSourceAvailable]);

  useEffect(() => {
    if (!markerSourceAvailable) return;
    if (!analysisMode && arrVideo.length > 1) {
      arrangeCheckboxCamera();
    }
    getMarkerByDebriefing(debriefingId, markerVideoId);
  }, [video, arrVideo, cameraMain, markerVideoId, cameraIndex, markerSourceAvailable, analysisMode]);

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const [checkedItems, setCheckedItems] = useState(
    arrVideo.reduce(
      (acc, item) => ({ ...acc, [item?.streaming_id]: false }),
      {}
    )
  );
  const [selectAll, setSelectAll] = useState(false);

  const handleSelectAllChange = (event) => {
    const { checked } = event.target;
    setSelectAll(checked);

    if (checked) {
      getMarkerByDebriefing(debriefingId);
    }

    if (!checked) {
      getMarkerByDebriefing(debriefingId, videoId);
    }

    setCheckedItems(
      arrVideo.reduce(
        (acc, item) => ({ ...acc, [item?.streaming_id]: checked }),
        {}
      )
    );
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const obj = checkedItems;

    setCheckedItems({
      ...checkedItems,
      [event.target.name]: event.target.checked,
    });

    let arrItems: any = [];

    if (event.target.checked) {
      arrItems.push(event.target.name);
    }

    if (!event.target.checked) {
      setSelectAll(false);
    }

    for (let i = 0; i < arrVideo.length; i++) {
      const element = arrVideo[i];
      if (
        obj[element?.streaming_id] == true &&
        element?.streaming_id != event.target.name
      ) {
        arrItems.push(element?.streaming_id);
      }
    }

    if (arrItems.length > 0) {
      const itens = arrItems.join(",");
      getMarkerByDebriefing(debriefingId, itens);
    }
  };

  const arrangeCheckboxCamera = () => {
    arrVideo.map((item: any, index: any) => {
      setCheckedItems({
        ...checkedItems,
        [item?.streaming_id]: false,
      });
    });
  };

  return (
    <Box ref={playerContainerRef} sx={{ scrollMarginTop: "80px" }}>
      <Box className={analysisPresentation ? undefined : stylesDebriefing.videoBar}
        sx={{ display: "flex", alignItems: "center", gap: 1, ...(analysisPresentation ? { mb: 1.5, flexWrap: "wrap" } : {}) }}>
        <Box className={stylesDebriefing.boxTitleVideo} sx={{ flex: 1, minWidth: 0, ...(analysisPresentation ? { gap: 1 } : {}) }}>
          {/* Só exibe o ícone se não for externo */}
          {analysisPresentation ? (
            <>
              <OndemandVideoIcon sx={{ color: palette.brand }} />
              <Typography sx={{ fontWeight: 700, fontSize: "0.95rem", color: palette.brandDark }}>
                Gravação da simulação
              </Typography>
            </>
          ) : !isExternal && (
            <VideocamOutlinedIcon
              sx={{ fontSize: 40 }}
              className={stylesDebriefing.cameraIconVideo}
            />
          )}
          {badge ? (
            <Box
              component="span"
              sx={{
                ml: 1.5,
                backgroundColor: palette.emeraldStrong,
                color: palette.surface,
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.025em",
                textTransform: "uppercase",
                borderRadius: "6px",
                padding: "4px 12px",
                whiteSpace: "nowrap",
              }}
            >
              {badge}
            </Box>
          ) : null}
          {analysisMode ? sources.map((source, idx) => (
            <Button
              key={source.source_id}
              variant={source.source_id === selectedSource?.source_id ? "contained" : "outlined"}
              aria-pressed={source.source_id === selectedSource?.source_id}
              sx={{ fontWeight: "bold", ml: analysisPresentation ? 0 : 2 }}
              onClick={() => changeCamera(idx)}
            >
              {cameraLabel(source.source_id)}
            </Button>
          )) : arrVideo &&
            arrVideo.map((v, idx) => (
              <Button
                sx={{
                  fontWeight: "bold",
                  ml: 2,
                  border: `1px solid ${v?.camera_color}`,
                  backgroundColor:
                    v?.streaming_id === videoId ? v?.camera_color : undefined,
                  color: v?.font_color,
                }}
                key={idx}
                onClick={() => changeCamera(idx)}
              >
                {v?.camera_name}
              </Button>
            ))}
          {captionUrl && !captionsForSource && (
            <Typography variant="caption" sx={{ ml: 2, color: analysisPresentation ? palette.textMuted : "white" }}>
              Legendas disponíveis na câmera principal.
            </Typography>
          )}
        </Box>
        {analysisPresentation && (
          <Chip label={`Posição: ${positionLabel}`} size="small" icon={<PlayCircleOutlineIcon />}
            sx={{ backgroundColor: `${palette.brand}15`, color: palette.brandDark, fontWeight: 700 }} />
        )}
        {onToggleMinimized && (
          <IconButton size="small" aria-expanded={!minimized} aria-controls={minimizedContentId}
            aria-label={minimized ? "Expandir vídeo" : "Minimizar vídeo"}
            onClick={onToggleMinimized}
            sx={{ color: palette.brandDark, bgcolor: palette.brandWash, "&:hover": { bgcolor: palette.brandSoft } }}>
            {minimized ? <KeyboardArrowDownIcon /> : <KeyboardArrowUpIcon />}
          </IconButton>
        )}
      </Box>
      <Box id={minimizedContentId} sx={{ display: minimized ? "none" : "block" }}>
      <Box sx={analysisPresentation ? {
        width: "100%", backgroundColor: "#000", borderRadius: "10px", overflow: "hidden",
        display: "flex", justifyContent: "center", alignItems: "center", boxShadow: palette.shadowSm,
        "& video": { objectFit: "contain" },
      } : undefined}>
      {effectiveVideo && !waitingForCaptions && (
        <ReactPlayer
          key={playerKey}
          url={effectiveVideo}
          playing={play}
          width={widthVideo ?? "100%"}
          height={heightVideo ?? "100%"}
          onProgress={handleProgress}
          onReady={handleReady}
          controls
          config={{
            file: {
              attributes: {
                crossOrigin: "anonymous",
              },
              tracks: activeCaptionUrl
                ? [{ kind: "captions", src: activeCaptionUrl, srcLang: "pt-BR", label: "Português", default: true }]
                : [],
            },
          }}
          ref={playerRef}
        />
      )}
      </Box>

      {(markersEnabled || (markerSourceAvailable && Array.isArray(markerTime) && markerTime.length > 0)) && <Box className={stylesDebriefing.videoBar}>
        <Box className={stylesDebriefing.boxMarker}>
          {markersEnabled && (
            <Box>
              <Box
                className={
                  progressVideo > 0
                    ? stylesDebriefing.boxNewMark
                    : stylesDebriefing.boxNewMarkRight
                }
              >
                {progressVideo > 0 && (
                  <Box className={stylesDebriefing.boxNewMarkButton}>
                    <LoadingButton
                      className={stylesDebriefing.btnNewMark}
                      onClick={getTimeVideo}
                      variant="contained"
                      loading={storeMarkerData.loading}
                    >
                      {profileId !== 5 && !storeMarkerData.loading ? (
                        t("title_debriefing_video_new_marking")
                      ) : (
                        <span>&nbsp;</span>
                      )}
                    </LoadingButton>

                    <Typography
                      sx={{ marginLeft: 4 }}
                      className={stylesDebriefing.textInfoNewMark}
                    >
                      {t("title_description_debriefing_video_markings")}
                    </Typography>
                  </Box>
                )}
                {!analysisMode && arrVideo.length > 1 && (
                  <ClickAwayListener onClickAway={handleClose}>
                    <div>
                      <Button
                        className={stylesDebriefing.buttonFilter}
                        id="basic-button"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                      >
                        <FilterAltIcon />
                      </Button>

                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        MenuListProps={{
                          "aria-labelledby": "basic-button",
                        }}
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "right",
                        }}
                        transformOrigin={{
                          vertical: "bottom",
                          horizontal: "left",
                        }}
                        sx={{
                          "& .MuiPaper-root": {
                            backgroundColor: colors.grayButtonCam2,
                          },
                        }}
                      >
                        <MenuItem className={stylesDebriefing.menuFilter}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectAll}
                                onChange={handleSelectAllChange}
                                name="selectAll"
                                color="secondary"
                              />
                            }
                            label="Todas"
                          />
                        </MenuItem>
                        {arrVideo.map((video, index) => (
                          <MenuItem
                            key={index}
                            className={stylesDebriefing.menuFilter}
                          >
                            <FormControlLabel
                              control={
                                <Checkbox
                                  color="secondary"
                                  onChange={handleCheckboxChange}
                                  name={video?.streaming_id}
                                  id={video?.streaming_id}
                                  checked={
                                    checkedItems[video?.streaming_id] ?? false
                                  }
                                />
                              }
                              label={video?.camera_name}
                            />
                          </MenuItem>
                        ))}
                      </Menu>
                    </div>
                  </ClickAwayListener>
                )}
              </Box>
            </Box>
          )}
          {markersEnabled && <Divider className={stylesDebriefing.divider} />}

          <Box className={stylesDebriefing.boxMarkerTime}>
            {markerSourceAvailable && Array.isArray(markerTime) &&
              markerTime.map((item, index) => (
                <TimerBox
                  onClick={() => handleModal(item)}
                  time={handleFormatTime(item?.time)}
                  cameraIndex={analysisMode ? selectedSource?.camera : cameraIndex}
                  key={index}
                  player={true}
                  arrVideo={arrVideo}
                  videoId={item?.video_id}
                />
              ))}
          </Box>
        </Box>
      </Box>}
      </Box>
    </Box>
  );
}
